import type {ReactElement} from 'react';
import React from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Button, Size, Type} from '@Components/button';
import type {CopyLinkButtonProps} from './copy-link-button.types';
import {useCopyToClipboardWithFeedback} from '@Hooks/useCopyToClipboardWithFeedback';
import {noop} from '@Utils/general.util';

export function CopyLinkButton({
  linkToCopy,
  tooltipTimeout,
  className = '',
  showIcon = false,
  buttonProps = {},
  iconProps = {icon: 'icon-link'},
  onCopyFeedbackComplete = noop,
  doChangeIconOnCopy = false,
  showSuccessColorIconOnCopy = false,
  tooltipText = window.i18next.t('pmwjs_link_copied'),
}: CopyLinkButtonProps): ReactElement<any> {
  const {onCopy, doShowFeedback} = useCopyToClipboardWithFeedback(linkToCopy, onCopyFeedbackComplete, tooltipTimeout);

  const onBtnClick = (e: React.MouseEvent): void => {
    onCopy();
    if (typeof buttonProps?.onClick === 'function') {
      void buttonProps.onClick(e);
    }

    if (typeof iconProps?.onClick === 'function') {
      iconProps.onClick(e);
    }
  };

  return (
    <OverlayTrigger
      overlay={<Tooltip className="spacing-m-b-2 _rounded-arrow">{tooltipText}</Tooltip>}
      delay={{show: 200, hide: 200}}
      placement="top"
      trigger={['focus', 'click']}
      show={doShowFeedback}
    >
      <div className="flexbox">
        {showIcon ? (
          <Icon
            shape={IconShape.SQUARE}
            size={IconSize.SIZE_ICON_20}
            type={IconType.SECONDARY}
            {...iconProps}
            icon="icon-link"
            ariaLabel={window.i18next.t('pmwjs_copy_link')}
            className={className}
            onClick={onBtnClick}
          />
        ) : (
          <Button
            size={Size.XSMALL}
            type={Type.GHOST}
            text={window.i18next.t('pmwjs_copy_link')}
            icon={doChangeIconOnCopy && doShowFeedback ? 'icon-check' : 'icon-link'}
            iconClassName={showSuccessColorIconOnCopy && doShowFeedback ? 'content-success' : ''}
            customClasses={className}
            isActive={doShowFeedback}
            {...buttonProps}
            onClick={onBtnClick}
          />
        )}
      </div>
    </OverlayTrigger>
  );
}
